import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.user_id;
  const storageKey = userId ? `currentWorkspaceId:${userId}` : "currentWorkspaceId";

  const [workspaces, setWorkspaces] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      if (data.length > 0) {
        // Check if a specific project was selected from ProjectHome
        const pendingProjectId = localStorage.getItem("crescendo:project_id");
        if (pendingProjectId) {
          localStorage.removeItem("crescendo:project_id");
          // Find first workspace belonging to this project
          const ws = data.find(w => w.project_id === pendingProjectId) || data[0];
          setCurrentId(ws.workspace_id);
          localStorage.setItem(storageKey, ws.workspace_id);
        } else {
          const savedId = localStorage.getItem(storageKey);
          const valid = data.find((w) => w.workspace_id === savedId);
          setCurrentId(valid ? savedId : data[0].workspace_id);
        }
      } else {
        setCurrentId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [storageKey]);

  // Reload whenever the active user changes (account switch).
  useEffect(() => { load(); }, [load, userId]);
  // Reload when workspace-folder assignment changes
  useEffect(() => {
    const onReload = () => load();
    window.addEventListener("crescendo:reload-workspaces", onReload);
    return () => window.removeEventListener("crescendo:reload-workspaces", onReload);
  }, [load]);

  const setCurrent = (id) => {
    setCurrentId(id);
    localStorage.setItem(storageKey, id);
  };

  const createWorkspace = async (name, color, description) => {
    const { data } = await api.post("/workspaces", { name, color, description });
    setWorkspaces((prev) => [...prev, data]);
    setCurrent(data.workspace_id);
    return data;
  };

  const deleteWorkspace = async (id) => {
    await api.delete(`/workspaces/${id}`);
    const remaining = workspaces.filter((w) => w.workspace_id !== id);
    setWorkspaces(remaining);
    if (currentId === id && remaining[0]) setCurrent(remaining[0].workspace_id);
  };

  const current = workspaces.find((w) => w.workspace_id === currentId) || null;

  return (
    <WorkspaceContext.Provider value={{ workspaces, setWorkspaces, current, currentId, setCurrent, createWorkspace, deleteWorkspace, loading, reload: load }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext);
